import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface FollowupInputProps {
  onSend: (message: string) => void;
  tokensUsed: number;
  tokensLimit: number;
  disabled?: boolean;
  isStreaming?: boolean;
}

export function FollowupInput({
  onSend,
  tokensUsed,
  tokensLimit,
  disabled = false,
  isStreaming = false,
}: FollowupInputProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!disabled && !isStreaming) {
      inputRef.current?.focus();
    }
  }, [disabled, isStreaming]);

  const handleSend = useCallback(() => {
    const trimmedInput = input.trim();
    if (!trimmedInput || disabled || isStreaming) return;

    if (tokensUsed >= tokensLimit) {
      return;
    }

    onSend(trimmedInput);
    setInput('');
  }, [input, disabled, isStreaming, tokensUsed, tokensLimit, onSend]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const tokensRemaining = tokensLimit - tokensUsed;
  const tokensPercentUsed = Math.round((tokensUsed / tokensLimit) * 100);

  return (
    <div className="border-t border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {t('common.askFollowup')}
        </span>
        <span className={`text-xs ${tokensPercentUsed > 80 ? 'text-amber-500' : 'text-slate-400'}`}>
          {t('common.tokensRemaining', { tokens: tokensRemaining.toLocaleString() })}
        </span>
      </div>
      <div className="flex gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Napisz pytanie..."
          disabled={disabled || isStreaming || tokensUsed >= tokensLimit}
          rows={2}
          className="flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-slate-700 dark:text-white disabled:opacity-50"
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || disabled || isStreaming || tokensUsed >= tokensLimit}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-end"
        >
          {isStreaming ? (
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </div>
      <p className="mt-1 text-xs text-slate-400">
        Enter aby wyslac, Shift+Enter dla nowej linii
      </p>
    </div>
  );
}

export default FollowupInput;
